/**
 * Markdown layer for the editor.
 *
 * Uses two OFFICIAL packages — no community wrappers, no vendored source:
 *  - `prosemirror-markdown` (Marijn Haverbeke, ProseMirror core team) for the
 *    MarkdownSerializer that walks the editor document and produces markdown.
 *  - `markdown-it` (used by Stack Overflow, Telegram Web, every static-site
 *    generator) for parsing markdown text into HTML.
 *
 * Strategy: SERIALIZE via prosemirror-markdown (schema-aware, accurate output);
 * PARSE via markdown-it → HTML → Tiptap's existing HTML parser. The HTML path
 * for parsing keeps our custom Mention and Attachment node `parseHTML` rules
 * working unchanged — only the serializer needs explicit per-node specs.
 *
 * No external runtime dependency on `tiptap-markdown` at all.
 */

import MarkdownIt from 'markdown-it'
import { MarkdownSerializer, defaultMarkdownSerializer } from 'prosemirror-markdown'
import DOMPurify from 'dompurify'
import { resolveAttachment } from './attachment-registry'
import { iconForMime, isInlineViewableMime, formatBytes, colorForMime, readableFileType } from './attachment-utility'

function _esc(rawValue) {
  return String(rawValue == null ? '' : rawValue)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// ---------------------------------------------------------------------------
// markdown-it instance with our two custom inline rules:
//   1. @[Name](kind:id)        — mention   → emits a <span class="mention">
//   2. [[kind:id]]              — attachment → emits a <video|audio|div ...>
// Tiptap's existing parseHTML rules on MentionWithLabel and Attachment pick
// these up, so we don't need a custom prosemirror-markdown parser at all.
// ---------------------------------------------------------------------------
const md = MarkdownIt('default', {
  html: true,
  linkify: true,
  breaks: false,
  typographer: false
})

md.inline.ruler.after('emphasis', 'mention', function mentionRule(state, silent) {
  const start = state.pos
  const src = state.src
  if (src.charCodeAt(start) !== 0x40 /* @ */) return false
  const match = /^@\[([^\]]+)\]\(([a-zA-Z-]+):([A-Za-z0-9_-]+)\)/.exec(src.slice(start))
  if (!match) return false
  if (!silent) {
    const label = match[1]
    const kind = match[2]
    const id = match[3]
    const html = `<span data-type="mention" class="mention" data-id="${_esc(id)}" data-label="${_esc(label)}" data-kind="${_esc(kind)}">@${_esc(label)}</span>`
    const token = state.push('html_inline', '', 0)
    token.content = html
    token.markup = '@'
  }
  state.pos += match[0].length
  return true
})

md.inline.ruler.after('emphasis', 'attachment_token', function attachmentRule(state, silent) {
  const start = state.pos
  const src = state.src
  if (src.charCodeAt(start) !== 0x5B /* [ */) return false
  if (src.charCodeAt(start + 1) !== 0x5B /* [ */) return false
  // Token forms:
  //   [[kind:id]]                          — no size
  //   [[kind:id|w=80%]]                    — width only
  //   [[kind:id|w=80%|h=auto]]              — both
  //   [[kind:id|h=300px]]                   — height only
  // Only video tokens are expected to carry size — but the parser is permissive.
  const match = /^\[\[(video|audio|file|image):([A-Za-z0-9_-]+)((?:\|[a-z]+=[^|\]]+)*)\]\]/.exec(src.slice(start))
  if (!match) return false
  if (!silent) {
    const kind = match[1]
    const id = match[2]
    const attrPart = match[3] || ''
    let width = null
    let height = null
    if (attrPart) {
      // attrPart looks like '|w=80%|h=auto'
      const attrPairs = attrPart.slice(1).split('|')
      for (const attrPair of attrPairs) {
        if (!attrPair) continue
        const equalsIndex = attrPair.indexOf('=')
        if (equalsIndex < 0) continue
        const attrKey = attrPair.slice(0, equalsIndex)
        const attrValue = attrPair.slice(equalsIndex + 1)
        if (attrKey === 'w') width = attrValue
        else if (attrKey === 'h') height = attrValue
      }
    }
    const attachment = resolveAttachment(id)
    let html
    if (attachment) {
      const url = attachment.fileURL || ''
      const name = attachment.fileName || ''
      const mime = attachment.mimeType || ''
      const size = attachment.fileSizeBytes || 0
      const sizeStyle = (width || height)
        ? ` style="${width ? 'width:' + _esc(width) + ';' : ''}${height ? 'height:' + _esc(height) + ';' : ''}max-width:100%"`
        : ''
      const widthAttr = width ? ` data-width="${_esc(width)}"` : ''
      const heightAttr = height ? ` data-height="${_esc(height)}"` : ''
      if (kind === 'video') {
        html = `<video src="${_esc(url)}" controls title="${_esc(name)}" data-mime="${_esc(mime)}" data-size="${size}" data-id="${_esc(id)}"${widthAttr}${heightAttr}${sizeStyle} class="md-media md-media-video"></video>`
      } else if (kind === 'audio') {
        html = `<audio src="${_esc(url)}" controls title="${_esc(name)}" data-mime="${_esc(mime)}" data-size="${size}" data-id="${_esc(id)}" class="md-media md-media-audio"></audio>`
      } else if (kind === 'image') {
        // [[image:id]] expands to a real <img>. New uploads from the editor
        // serialize to ![alt](url) (standard markdown image), so this token
        // form is mostly used by hand-written / seeded bodies that
        // explicitly reference an image attachment by id.
        html = `<img src="${_esc(url)}" alt="${_esc(name)}" title="${_esc(name)}" data-id="${_esc(id)}" class="md-inline-image"${sizeStyle}>`
      } else {
        // PDFs open in a new tab via target=_blank — the browser previews
        // them natively. Other file types use the download attribute so
        // the browser saves them instead of navigating. Same-origin or a
        // server-side Content-Disposition header is required for the
        // download to actually trigger; cross-origin without that header
        // falls back to navigation.
        //
        // The card is wrapped in a block-level <div data-type="attachment">.
        // Without it, Tiptap's Link mark claims the inner <a> on edit-load
        // (since <a> is intrinsically inline) and the [[file:id]] token
        // degrades to a plain markdown link [label](url) on save. The
        // browser auto-closes the implicit <p> around the <div> so it
        // parses as a sibling block, where the Attachment node's
        // div[data-type="attachment"] parseHTML rule wins.
        const sub = formatBytes(size) + ' &middot; ' + _esc(readableFileType(mime, name))
        const iconClass = iconForMime(mime)
        const tile = colorForMime(mime)
        const isInline = isInlineViewableMime(mime)
        const linkAttrs = isInline
          ? `target="_blank" rel="noopener noreferrer"`
          : `download="${_esc(name)}" rel="noopener noreferrer"`
        const actionIcon = isInline ? 'fa-external-link-alt' : 'fa-download'
        const actionLabel = isInline ? 'Open' : 'Download'
        html = `<div data-type="attachment" data-id="${_esc(id)}" data-src="${_esc(url)}" data-kind="file" data-name="${_esc(name)}" data-mime="${_esc(mime)}" data-size="${size}" class="md-attachment-block">`
          + `<a href="${_esc(url)}" ${linkAttrs} `
             + `class="md-attachment md-attachment-link" title="${_esc(actionLabel)} ${_esc(name)}">`
            + `<span class="md-attachment-left" style="background:${_esc(tile.bg)}">`
              + `<i class="fas ${iconClass}" style="color:${_esc(tile.color)}"></i>`
            + `</span>`
            + `<span class="md-attachment-meta"><span class="md-attachment-name">${_esc(name)}</span><span class="md-attachment-sub">${sub}</span></span>`
            + `<span class="md-attachment-action" aria-label="${_esc(actionLabel)}"><i class="fas ${actionIcon}"></i></span>`
          + `</a>`
        + `</div>`
      }
    } else {
      html = `<div data-type="attachment" data-id="${_esc(id)}" data-kind="file" data-name="Missing attachment (${_esc(id)})" data-mime="application/octet-stream" data-size="0" class="md-attachment md-attachment-missing">Missing attachment</div>`
    }
    const token = state.push('html_inline', '', 0)
    token.content = html
    token.markup = '[['
  }
  state.pos += match[0].length
  return true
})

// ---------------------------------------------------------------------------
// PROSEMIRROR-MARKDOWN SERIALIZER (Tiptap doc → markdown string)
//
// Tiptap uses camelCase schema names (italic, bold, bulletList, codeBlock,
// hardBreak, horizontalRule, listItem, orderedList) while prosemirror-markdown's
// defaults use legacy snake_case names. We re-key each entry so the serializer
// can find a spec for every node/mark in our schema.
// ---------------------------------------------------------------------------
const pmNodes = defaultMarkdownSerializer.nodes
const pmMarks = defaultMarkdownSerializer.marks

// Table serializer (GFM pipe tables). prosemirror-markdown doesn't ship one;
// this walks each cell, captures its inline markdown by temporarily swapping
// state.out, then assembles the table with a header separator row.
function serializeTable(state, node) {
  const rows = []
  let columnCount = 0

  node.forEach((row) => {
    const cells = []
    row.forEach((cell) => {
      // Capture cell content into a sub-buffer
      const savedOut = state.out
      state.out = ''
      // A table cell typically contains a paragraph; render its inline content
      cell.forEach((child) => {
        if (child.type.name === 'paragraph') {
          state.renderInline(child)
        } else {
          // For non-paragraph block content (rare in tables), render normally
          // and flatten newlines below.
          state.render(child, cell, 0)
        }
      })
      const cellMd = state.out.trim().replace(/\n+/g, ' ').replace(/\|/g, '\\|') || ' '
      state.out = savedOut
      cells.push(cellMd)
    })
    columnCount = Math.max(columnCount, cells.length)
    rows.push(cells)
  })

  if (rows.length === 0 || columnCount === 0) {
    state.closeBlock(node)
    return
  }
  // Pad short rows
  rows.forEach((rowCells) => { while (rowCells.length < columnCount) rowCells.push(' ') })
  // First row is the header in our schema; GFM requires a separator row after it
  state.write('| ' + rows[0].join(' | ') + ' |\n')
  state.write('|' + ' --- |'.repeat(columnCount) + '\n')
  for (let rowIndex = 1; rowIndex < rows.length; rowIndex++) {
    state.write('| ' + rows[rowIndex].join(' | ') + ' |\n')
  }
  state.closeBlock(node)
}

// Task item — GFM checkbox: "- [ ] text" or "- [x] text"
function serializeTaskItem(state, node) {
  const checked = node.attrs && node.attrs.checked ? 'x' : ' '
  state.write(`- [${checked}] `)
  state.renderContent(node)
  state.closeBlock(node)
}

const serializer = new MarkdownSerializer(
  {
    paragraph: pmNodes.paragraph,
    heading: pmNodes.heading,
    blockquote: pmNodes.blockquote,
    bulletList: pmNodes.bullet_list,
    orderedList: pmNodes.ordered_list,
    listItem: pmNodes.list_item,
    codeBlock: pmNodes.code_block,
    horizontalRule: pmNodes.horizontal_rule,
    hardBreak: pmNodes.hard_break,
    text: pmNodes.text,

    // Tables and task lists (Tiptap has these extensions)
    table: serializeTable,
    tableRow: (state, node) => state.renderContent(node),
    tableCell: (state, node) => state.renderInline(node),
    tableHeader: (state, node) => state.renderInline(node),
    taskList: pmNodes.bullet_list,
    taskItem: serializeTaskItem,

    // Image is block-level in our schema (inline: false on the SizedImage
    // extension). Write inline HTML when an explicit width/height style is
    // set, else standard CommonMark ![alt](url). Always closeBlock so the
    // next block (paragraph, attachment) lands on its own line.
    image(state, node) {
      const { src, alt, title, style } = node.attrs || {}
      if (style) {
        const escapedAlt = String(alt || '').replace(/"/g, '&quot;')
        const titleAttr = title ? ` title="${String(title).replace(/"/g, '&quot;')}"` : ''
        const escapedStyle = String(style).replace(/"/g, '&quot;')
        state.write(`<img src="${src}" alt="${escapedAlt}"${titleAttr} style="${escapedStyle}">`)
      } else {
        state.write('![' + state.esc(alt || '') + '](' + state.esc(src || '') +
          (title ? ' ' + state.quote(title) : '') + ')')
      }
      state.closeBlock(node)
    },

    // Mention: inline custom syntax @[Name](kind:id)
    mention(state, node) {
      const { id = '', label = '', kind = 'user' } = node.attrs || {}
      // Defensive: only accept primitive labels. If something upstream
      // passed an object (e.g. the whole user record instead of .name),
      // String() would emit "[object Object]" into the markdown — which
      // then renders as the literal text on every future load. Fall back
      // to the id so the mention still resolves.
      const labelStr = (typeof label === 'string' || typeof label === 'number')
        ? String(label).trim()
        : ''
      const safeLabel = (labelStr || String(id)).replace(/\]/g, '\\]')
      state.write(`@[${safeLabel}](${kind}:${id})`)
    },

    // Attachment: block token [[kind:id]] referencing message_attachments table.
    // Optional size attributes for video: [[video:id|w=80%|h=auto]]
    attachment(state, node) {
      const { id, kind = 'file', width, height } = node.attrs || {}
      if (id == null) {
        state.write('[[unregistered-attachment]]')
      } else {
        let attachmentToken = `[[${kind}:${id}`
        if (width) attachmentToken += `|w=${width}`
        if (height) attachmentToken += `|h=${height}`
        attachmentToken += ']]'
        state.write(attachmentToken)
      }
      state.closeBlock(node)
    }
  },
  {
    italic: pmMarks.em,
    bold: pmMarks.strong,
    code: pmMarks.code,
    link: pmMarks.link,
    strike: { open: '~~', close: '~~', mixable: true, expelEnclosingWhitespace: true },
    underline: { open: '<u>', close: '</u>', mixable: true, expelEnclosingWhitespace: true }
  }
)

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Serialize the editor's current document to markdown. */
export function toMarkdown(editor) {
  if (!editor || !editor.state || !editor.state.doc) return ''
  return serializer.serialize(editor.state.doc)
}

// Strict allowlist for stored bodies that hit `v-html`. Mirrors the editor-
// paste config in MarkdownEditor.vue (re-imports this exact constant so the
// two paths can never drift). Legacy / API-poisoned content can't smuggle
// <script>, on* handlers, or unsafe URI schemes past us. Editor input was
// already sanitized on paste, but bodies stored before that pipeline (or
// inserted by another client) pass through this on every render.
//
// `target`, `rel`, and `download` are intentionally allowed because the
// attachment-card renderer below emits `<a target="_blank" rel="noopener"
// download="…">` for downloadable files — without those in the allowlist
// DOMPurify silently strips them and the download UX breaks.
export const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'hr',
    'strong', 'em', 'u', 's', 'code', 'pre', 'span',
    'ul', 'ol', 'li',
    'blockquote',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'a', 'img', 'video', 'audio',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'div', 'input',
    // <i> is needed for FontAwesome icons inside attachment cards
    // (e.g. <i class="fas fa-file-pdf"></i>). Without this in the
    // allowlist, DOMPurify strips the <i> and the colored mime tile
    // renders empty.
    'i'
  ],
  ALLOWED_ATTR: [
    'href', 'title', 'alt', 'src', 'controls', 'class',
    'data-type', 'data-id', 'data-src', 'data-kind',
    'data-name', 'data-mime', 'data-size', 'data-label',
    'width', 'height', 'style', 'colspan', 'rowspan',
    'checked', 'disabled', 'type',
    'target', 'rel', 'download'
  ],
  ALLOWED_URI_REGEXP: /^(?:https?:\/\/|mailto:|tel:|\/|\.\/|#|blob:|data:image\/(?:png|jpe?g|gif|webp|svg\+xml))/i,
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'meta', 'link', 'style'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onsubmit', 'autofocus']
}

// Single sanitize entry point. Exported so the editor can call it on its
// own paste/setContent paths instead of carrying a duplicate config.
export function purifyHTML(html) {
  if (!html) return ''
  return DOMPurify.sanitize(html, PURIFY_CONFIG)
}

/**
 * Convert markdown text to HTML using markdown-it + our custom rules,
 * THEN sanitize the result with DOMPurify before returning. Every
 * `v-html` binding in the forum surface routes through this function — sanitizing
 * here closes the XSS path that markdown-it's `html: true` opens for
 * stored bodies that contain raw HTML.
 */
export function markdownToHTML(markdown) {
  return purifyHTML(md.render(markdown || ''))
}
